import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { MAILBOX_FEATURE_SET } from '@stitch/pbx-adapter/catalog';
import { describeReferences, referencesTo, suggestNumber } from '@stitch/pbx-adapter/describe';
import { companyLengthFrom, extensionHint, extensionProblem } from '@stitch/pbx-adapter/extensions';
import { api, transcribed, type Intent, type PersonRow } from '../../api/client';
import { ReviewDrawer } from '../../components/ReviewDrawer';
import { VoicemailGreetingLoaded } from '../../components/VoicemailGreeting';
import { useTranscriptSwitch } from '../../components/TranscriptSwitch';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { plural } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

type Box = { extension: string; name: string; email: string };
type Staged = { op: 'add'; box: Box } | { op: 'edit'; extension: string; was: string; set: Partial<Box> } | { op: 'remove'; extension: string; name: string };

const nameOf = (p: Pick<PersonRow, 'firstName' | 'lastName'>) => `${p.firstName} ${p.lastName}`.trim();
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function intentOf(s: Staged): Intent {
  switch (s.op) {
    case 'add':
      return {
        op: 'account.create',
        kind: 'person',
        extension: s.box.extension,
        values: { firstName: s.box.name, lastName: '', featureSet: MAILBOX_FEATURE_SET, ...(s.box.email ? { email: s.box.email } : {}) }
      };
    case 'edit':
      return {
        op: 'person.update',
        extension: s.extension,
        set: { ...(s.set.name !== undefined ? { firstName: s.set.name } : {}), ...(s.set.email !== undefined ? { email: s.set.email } : {}) }
      };
    case 'remove':
      return { op: 'person.delete', extension: s.extension };
  }
}

function describe(s: Staged): string {
  switch (s.op) {
    case 'add':
      return `Add the voicemail box ${s.box.name} at ${s.box.extension}${s.box.email ? `, messages emailed to ${s.box.email}` : ''}`;
    case 'edit':
      return `Change the voicemail box ${s.was} (${s.extension}): ${[s.set.name !== undefined && `name → ${s.set.name}`, s.set.email !== undefined && `messages emailed to ${s.set.email || '(nobody)'}`].filter(Boolean).join(', ')}`;
    case 'remove':
      return `Remove the voicemail box ${s.name} (${s.extension})`;
  }
}

/**
 * Voicemail boxes: extensions with the Voicemail only feature set (a department's mailbox, say).
 * Kept apart from people, who have phones.
 */
export function TenantVoicemail() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const people = useData(() => api.people(d), [d]);
  const dir = useData(() => api.directory(d).catch(() => null), [d]);
  const routing = useData(() => api.routing(d).catch(() => null), [d]);
  // Voicemail transcripts for each box, when Stitch has the service (admins turn the company on first).
  const settings = useData(() => api.companySettings(d).catch(() => null), [d], { live: false });
  const transcripts = settings.data?.editable && settings.data.transcripts?.available ? settings.data.transcripts : null;
  const writeOut = useTranscriptSwitch(d, settings.reload);
  const [staged, setStaged] = useState<Staged[]>([]);
  const [dialog, setDialog] = useState<{ mode: 'add' } | { mode: 'edit'; box: PersonRow } | null>(null);
  /** The voicemail box whose greeting is open (1.12.33). */
  const [greeting, setGreeting] = useState<PersonRow | null>(null);
  const [dialogKey, setDialogKey] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const [reviewKey, setReviewKey] = useState(0);
  const boxes = useMemo(() => (people.data?.people ?? []).filter((p) => p.featureSet === MAILBOX_FEATURE_SET), [people.data]);
  const adds = staged.filter((s): s is Extract<Staged, { op: 'add' }> => s.op === 'add');
  // Adding one needs the feature set to be settable; changing names and emails doesn't.
  const canAdd = tenant.writable && Boolean(people.data?.writableFields.includes('featureSet'));
  const canRemove = tenant.writable && Boolean(people.data?.canRemove);
  const entries = [...(dir.data?.entries ?? []), ...adds.map((a) => ({ extension: a.box.extension, kind: 'person' as const, featureSet: MAILBOX_FEATURE_SET }))];
  // New extensions: 3 to 6 digits, as many as the company's others (2986).
  const company = companyLengthFrom(entries);
  const opts = { ...(dir.data?.mailboxPrefix ? { mailboxPrefix: dir.data.mailboxPrefix } : {}), ...(company ? { length: company.length } : {}) };
  const isBox = (e: (typeof entries)[number]) => e.kind === 'person' && 'featureSet' in e && e.featureSet === MAILBOX_FEATURE_SET;
  // After the last voicemail box; the first one follows the last person.
  const suggested = entries.some(isBox)
    ? suggestNumber('person', entries.map((e) => (isBox(e) ? e : { ...e, kind: 'other' as const })), opts)
    : suggestNumber('person', entries, opts);
  const open = (dl: NonNullable<typeof dialog>) => {
    setDialogKey((k) => k + 1);
    setDialog(dl);
  };
  const stagedFor = (ext: string) => staged.filter((s) => (s.op === 'add' ? s.box.extension : s.extension) === ext);
  const usedBy = (ext: string) => referencesTo(ext, routing.data?.accounts ?? []);

  return (
    <>
      <PageHeader
        eyebrow={people.data ? plural(boxes.length, 'voicemail box', 'voicemail boxes') : d}
        title="Voicemail boxes"
        actions={
          <Button
            variant="primary"
            icon="plus"
            disabled={!canAdd}
            title={canAdd ? undefined : 'Adding voicemail boxes needs feature sets to be confirmed on this phone system'}
            onClick={() => open({ mode: 'add' })}
          >
            Add a voicemail box
          </Button>
        }
      />
      <div className="content">
        <Notice title="Mailboxes without a phone">
          For a department or a shared line: callers leave a message, and it’s emailed to whoever looks after it. Send calls here from an auto attendant key, or when nobody answers
          a ring group.
        </Notice>
        {tenant.writable && !canAdd && people.data && (
          <Notice tone="warn" icon="lock">
            Adding new voicemail boxes waits until feature sets are confirmed on this phone system. You can already rename them and change where messages go.
          </Notice>
        )}
        {writeOut.error && <Notice tone="crit">{writeOut.error}</Notice>}
        <Card>
          {people.error ? (
            <LoadError error={people.error} retry={people.reload} />
          ) : !people.data ? (
            <Loading label="Loading voicemail boxes" />
          ) : boxes.length === 0 && adds.length === 0 ? (
            <Empty title="No voicemail boxes yet">An extension set to Voicemail only shows up here.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Ext</th>
                    <th scope="col">Name</th>
                    <th scope="col" className="hide-sm">
                      Messages emailed to
                    </th>
                    <th scope="col" className="hide-sm">
                      Used by
                    </th>
                    {transcripts && (
                      <th scope="col" className="hide-sm" title="Voicemail written out, so it can be read without playing it">
                        Written out
                      </th>
                    )}
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {adds.map((a) => (
                    <tr key={`add-${a.box.extension}`} className="staged">
                      <td className="mono">{a.box.extension}</td>
                      <td>
                        {a.box.name}{' '}
                        <Chip small tone="brand">
                          New
                        </Chip>
                      </td>
                      <td className="hide-sm">{a.box.email || <span className="muted">–</span>}</td>
                      <td className="hide-sm muted">–</td>
                      {transcripts && <td className="hide-sm muted">–</td>}
                      <td>
                        <div className="row-actions">
                          <button type="button" className="icon-btn" aria-label={`Cancel adding ${a.box.name}`} onClick={() => setStaged((s) => s.filter((x) => x !== a))}>
                            <Icon name="x" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {boxes.map((b) => {
                    const mine = stagedFor(b.extension);
                    const edit = mine.find((s): s is Extract<Staged, { op: 'edit' }> => s.op === 'edit');
                    const removing = mine.some((s) => s.op === 'remove');
                    const refs = usedBy(b.extension);
                    return (
                      <tr key={b.extension} className={removing ? 'staged removed' : mine.length ? 'staged' : ''}>
                        {/* The box's own page: its greeting and what it does with old messages (1.12.33). */}
                        <td className="mono">
                          <Link to={`/t/${encodeURIComponent(d)}/people/${encodeURIComponent(b.extension)}`} aria-label={`Open voicemail box ${b.extension}`}>
                            {b.extension}
                          </Link>
                        </td>
                        <td>
                          {edit?.set.name ?? nameOf(b)}{' '}
                          {edit && (
                            <Chip small tone="brand">
                              Edited
                            </Chip>
                          )}{' '}
                          {removing && (
                            <Chip small tone="crit">
                              Removing
                            </Chip>
                          )}
                        </td>
                        <td className="hide-sm">{(edit?.set.email ?? b.email) || <span className="muted">Nobody yet</span>}</td>
                        <td className="hide-sm">{refs.length ? describeReferences(refs) : <span className="muted">Nothing sends calls here</span>}</td>
                        {transcripts && (
                          <td className="hide-sm">
                            <input
                              type="checkbox"
                              aria-label={`Write out ${nameOf(b)}’s voicemail`}
                              checked={transcribed(transcripts, b.extension)}
                              disabled={Boolean(writeOut.busy)}
                              onChange={(e) =>
                                void writeOut.change({ mailboxes: [b.extension], own: false, label: `${nameOf(b)}’s voicemail` }, e.target.checked, {
                                  company: transcripts.on,
                                  canTurnOnCompany: true
                                })
                              }
                            />
                          </td>
                        )}
                        <td>
                          <div className="row-actions">
                            {mine.length ? (
                              <Button size="small" variant="ghost" onClick={() => setStaged((s) => s.filter((x) => !mine.includes(x)))}>
                                Cancel changes
                              </Button>
                            ) : (
                              <>
                                <button type="button" className="icon-btn" aria-label={`${nameOf(b)}’s greeting`} title="Greeting: what callers hear" onClick={() => setGreeting(b)}>
                                  <Icon name="volume" size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="icon-btn"
                                  aria-label={`Edit ${nameOf(b)}`}
                                  title="Edit"
                                  disabled={!tenant.writable}
                                  onClick={() => open({ mode: 'edit', box: b })}
                                >
                                  <Icon name="edit" size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="icon-btn"
                                  aria-label={`Remove ${nameOf(b)}`}
                                  title={refs.length ? `Used by ${describeReferences(refs)}: change that first` : 'Remove'}
                                  disabled={!canRemove || refs.some((r) => r.how === 'route')}
                                  onClick={() => setStaged((s) => [...s, { op: 'remove', extension: b.extension, name: nameOf(b) }])}
                                >
                                  <Icon name="trash" size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <p className="muted small">
          To turn a voicemail box into someone’s extension (or the other way round), change its feature set on <Link to={`/t/${encodeURIComponent(d)}/people`}>People</Link>.
        </p>
      </div>

      {staged.length > 0 && !reviewing && (
        <div className="tray" role="region" aria-label="Changes to review">
          <div className="grow">
            <strong>{plural(staged.length, 'change')} ready to review</strong>
            <span>Nothing has changed on the phone system yet.</span>
          </div>
          <Button variant="ghost" onClick={() => setStaged([])}>
            Clear
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setReviewKey((k) => k + 1);
              setReviewing(true);
            }}
          >
            Review changes
          </Button>
        </div>
      )}

      {dialog && (
        <BoxDialog
          key={dialogKey}
          state={dialog}
          suggested={suggested}
          company={company}
          taken={(ext) => entries.some((e) => e.extension === ext)}
          onClose={() => setDialog(null)}
          onSave={(box, original) => {
            if (!original) setStaged((s) => [...s, { op: 'add', box }]);
            else {
              const set: Partial<Box> = {};
              if (box.name !== nameOf(original)) set.name = box.name;
              if (box.email !== original.email) set.email = box.email;
              if (Object.keys(set).length)
                setStaged((s) => [
                  ...s.filter((x) => !(x.op === 'edit' && x.extension === original.extension)),
                  { op: 'edit', extension: original.extension, was: nameOf(original), set }
                ]);
            }
            setDialog(null);
          }}
        />
      )}

      <ReviewDrawer
        key={reviewKey}
        open={reviewing}
        tenant={d}
        items={staged.map((s, i) => ({ key: `${s.op}-${i}`, description: describe(s), intent: intentOf(s) }))}
        onUnstage={(key) => setStaged((cur) => cur.filter((_, i) => `${cur[i]!.op}-${i}` !== key))}
        onClose={(applied) => {
          setReviewing(false);
          if (applied) {
            setStaged([]);
            people.reload();
            dir.reload();
            routing.reload();
          }
        }}
      />
      {writeOut.dialog}
      {greeting && (
        <Dialog
          open
          onClose={() => setGreeting(null)}
          title={`${nameOf(greeting)}’s greeting`}
          subtitle={`Voicemail box ${greeting.extension}: what callers hear before they leave a message.`}
          foot={
            <Button variant="ghost" onClick={() => setGreeting(null)}>
              Close
            </Button>
          }
        >
          <VoicemailGreetingLoaded domain={d} extension={greeting.extension} own={false} />
        </Dialog>
      )}
    </>
  );
}

function BoxDialog({
  state,
  suggested,
  company,
  taken,
  onClose,
  onSave
}: {
  state: { mode: 'add' } | { mode: 'edit'; box: PersonRow };
  suggested: string;
  company: ReturnType<typeof companyLengthFrom>;
  taken: (ext: string) => boolean;
  onClose: () => void;
  onSave: (box: Box, original?: PersonRow) => void;
}) {
  const original = state.mode === 'edit' ? state.box : undefined;
  const [extension, setExtension] = useState(original?.extension ?? suggested);
  const [name, setName] = useState(original ? nameOf(original) : '');
  const [email, setEmail] = useState(original?.email ?? '');
  const [tried, setTried] = useState(false);
  const errors = {
    extension: !original && (extensionProblem(extension, company) ?? (taken(extension) ? 'That number is already used.' : '')),
    name: !name.trim() ? 'Give it a name, such as Billing.' : '',
    email: email.trim() && !EMAIL.test(email.trim()) ? 'Not a valid email address.' : ''
  };
  const ok = !errors.extension && !errors.name && !errors.email;
  return (
    <Dialog
      open
      onClose={onClose}
      title={original ? `Edit ${nameOf(original)}` : 'Add a voicemail box'}
      subtitle={original ? `Extension ${original.extension}` : 'A mailbox with no phone.'}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setTried(true);
              if (ok) onSave({ extension, name: name.trim(), email: email.trim() }, original);
            }}
          >
            {original ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      {!original && (
        <Field
          label="Extension"
          value={extension}
          onChange={(e) => setExtension(e.target.value.trim())}
          className="input mono"
          error={tried ? errors.extension || undefined : undefined}
          hint={extension === suggested ? 'The next free number. Change it if you like.' : extensionHint(company)}
        />
      )}
      <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Billing" error={tried ? errors.name || undefined : undefined} data-autofocus />
      <Field
        label="Email messages to"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="billing@yourcompany.com"
        error={tried ? errors.email || undefined : undefined}
        hint="Each new message arrives as an email with the recording."
      />
    </Dialog>
  );
}
